import { defineCopy, type Widen } from "../define.ts";

/** Blocks (a thread's result, an insight, a page, a measure page) and the thread they are shown in. */
const en = {
  blocks: {
    chart: {
      now: "Now",
      before: "Before",
      against: "Against the period before",
      name: "Name",
      value: "Value",
      share: "Share",
      weekdays: "Mon,Tue,Wed,Thu,Fri,Sat,Sun",
      dropped: "dropped off",
      preliminary: "Not final yet",
      preliminaryNote: "The shaded part is too recent to be final. It may still change.",
      from: "from {{count}}",
      fromWhat: "from {{count}} {{what}}",
    },
    number: {
      change: "{{change}} against before",
      changeDay: "{{change}} against the day before",
      day: "{{day}}",
      finalDay: "{{day}}, the latest final day",
      normal: "Normally between {{low}} and {{high}}",
      forming: "Adea is still learning what is normal here",
    },
    table: {
      truncated: "Showing {{shown}} of {{total}} rows",
      more: "{{count}} more",
    },
    code: {
      source: "In the code:",
      replay: "Play it again",
      flow: "How it works",
      tree: "How it works, part by part",
      expand: "Show the parts under it",
      collapse: "Hide the parts under it",
    },
    graph: {
      label: "How they connect",
      one_to_many: "one to many",
      many_to_one: "many to one",
      one_to_one: "one to one",
      many_to_many: "many to many",
      flow: "feeds",
    },
    timeline: {
      label: "What happened",
      by: "by {{actor}}",
    },
    callout: {
      fact: "Worth knowing",
      warning: "Watch out",
      good: "Good news",
    },
    analysis: {
      method: "How it was worked out",
      caveats: "Keep in mind",
      cohort: {
        corner: "Started",
        size: "People",
        all: "All",
        measure: "still there",
        tip: "{{share}} {{measure}} · {{kept}} of {{size}}",
        quarter: "Q{{q}} {{year}}",
        week: "Week of {{date}}",
        since: {
          hour: "Hours since",
          day: "Days since",
          week: "Weeks since",
          month: "Months since",
          quarter: "Quarters since",
          year: "Years since",
        },
        at: {
          hour: "hour {{n}}",
          day: "day {{n}}",
          week: "week {{n}}",
          month: "month {{n}}",
          quarter: "quarter {{n}}",
          year: "year {{n}}",
        },
      },
      forecast: {
        actual: "Actual",
        ahead: "Forecast",
        range: "Where it will most likely land",
      },
      test: {
        veryLikely: "Very likely a real difference",
        likely: "Likely a real difference",
        maybe: "Could be chance",
        unclear: "No clear difference",
        difference: "Difference",
        points: "{{n}} points",
        more: "{{share}} more",
        less: "{{share}} less",
        range: "The real difference is most likely between {{low}} and {{high}} ({{level}} confidence).",
        n: "of {{n}}",
      },
      spread: {
        median: "Median",
        mean: "Average",
        middle: "Middle 80%",
        count: "Values",
      },
    },
  },
  thread: {
    you: "You",
    adea: "Adea",
    thinking: "Thinking…",
    rail: "What Adea looked at",
    steps_one: "Looked at {{count}} thing",
    steps_other: "Looked at {{count}} things",
    showSteps: "Show what Adea looked at",
    hideSteps: "Hide",
    step: {
      schema: "Looking through the tables",
      table: "Reading the {{name}} table",
      query: "Writing a query",
      adjust: "Adjusting the query",
      record: "Finding {{name}}",
      codeSearch: "Searching the code for {{name}}",
      code: "Reading {{name}}",
      history: "Reading the code's history",
      python: "Working it out",
      understanding: "Reading what Adea knows about {{name}}",
      recipe: "Running {{name}}",
      spawn: "Looking at several things at once",
      ask: "Asking you",
      other: "Working",
    },
    failedStep: "Didn't work: {{reason}}",
    asks: "Adea asks",
    asked: "You answered: {{answer}}",
    failed: "Adea couldn't finish this answer.",
    retry: "Try again",
    stopped: "Stopped before it was done.",
    followUp: "Ask a follow-up",
    send: "Send",
    stop: "Stop",
    attach: "Add a picture",
    removeAttachment: "Remove picture",
    picture: "A picture you added",
    keep: "Keep this",
    askAbout: "Ask about this",
    source: "Source",
    sources: "What this rests on",
    explore: "Explore",
  },
};

/** Blokke (svaret i en tråd, en indsigt, en side, et måltals side) og tråden, de vises i. */
const da: Widen<typeof en> = {
  blocks: {
    chart: {
      now: "Nu",
      before: "Før",
      against: "I forhold til perioden før",
      name: "Navn",
      value: "Værdi",
      share: "Andel",
      weekdays: "man,tir,ons,tor,fre,lør,søn",
      dropped: "faldt fra",
      preliminary: "Ikke endeligt endnu",
      preliminaryNote: "Det skraverede er for nyt til at være endeligt. Det kan stadig ændre sig.",
      from: "ud af {{count}}",
      fromWhat: "ud af {{count}} {{what}}",
    },
    number: {
      change: "{{change}} i forhold til før",
      changeDay: "{{change}} i forhold til dagen før",
      day: "{{day}}",
      finalDay: "{{day}}, seneste endelige dag",
      normal: "Normalt mellem {{low}} og {{high}}",
      forming: "Adea lærer stadig, hvad der er normalt her",
    },
    table: {
      truncated: "Viser {{shown}} af {{total}} rækker",
      more: "{{count}} mere",
    },
    code: {
      source: "I koden:",
      replay: "Afspil igen",
      flow: "Sådan fungerer det",
      tree: "Sådan fungerer det, del for del",
      expand: "Vis delene under",
      collapse: "Skjul delene under",
    },
    graph: {
      label: "Sådan hænger de sammen",
      one_to_many: "én til mange",
      many_to_one: "mange til én",
      one_to_one: "én til én",
      many_to_many: "mange til mange",
      flow: "fører til",
    },
    timeline: {
      label: "Det skete",
      by: "af {{actor}}",
    },
    callout: {
      fact: "Værd at vide",
      warning: "Pas på",
      good: "Godt nyt",
    },
    analysis: {
      method: "Sådan er det regnet ud",
      caveats: "Værd at vide",
      cohort: {
        corner: "Startede",
        size: "Antal",
        all: "Alle",
        measure: "stadig med",
        tip: "{{share}} {{measure}} · {{kept}} af {{size}}",
        quarter: "{{q}}. kvt. {{year}}",
        week: "Ugen fra {{date}}",
        since: {
          hour: "Timer efter",
          day: "Dage efter",
          week: "Uger efter",
          month: "Måneder efter",
          quarter: "Kvartaler efter",
          year: "År efter",
        },
        at: {
          hour: "time {{n}}",
          day: "dag {{n}}",
          week: "uge {{n}}",
          month: "måned {{n}}",
          quarter: "kvartal {{n}}",
          year: "år {{n}}",
        },
      },
      forecast: {
        actual: "Faktisk",
        ahead: "Prognose",
        range: "Hvor det med størst sandsynlighed lander",
      },
      test: {
        veryLikely: "Med stor sandsynlighed en reel forskel",
        likely: "Sandsynligvis en reel forskel",
        maybe: "Kan være tilfældigt",
        unclear: "Ingen tydelig forskel",
        difference: "Forskel",
        points: "{{n}} procentpoint",
        more: "{{share}} mere",
        less: "{{share}} mindre",
        range:
          "Den reelle forskel ligger med størst sandsynlighed mellem {{low}} og {{high}} ({{level}} sikkerhed).",
        n: "af {{n}}",
      },
      spread: {
        median: "Median",
        mean: "Gennemsnit",
        middle: "Midterste 80 %",
        count: "Værdier",
      },
    },
  },
  thread: {
    you: "Dig",
    adea: "Adea",
    thinking: "Tænker …",
    rail: "Det kiggede Adea på",
    steps_one: "Kiggede på {{count}} ting",
    steps_other: "Kiggede på {{count}} ting",
    showSteps: "Vis, hvad Adea kiggede på",
    hideSteps: "Skjul",
    step: {
      schema: "Kigger tabellerne igennem",
      table: "Læser tabellen {{name}}",
      query: "Skriver en forespørgsel",
      adjust: "Retter forespørgslen til",
      record: "Finder {{name}}",
      codeSearch: "Søger efter {{name}} i koden",
      code: "Læser {{name}}",
      history: "Læser kodens historik",
      python: "Regner det ud",
      understanding: "Læser, hvad Adea ved om {{name}}",
      recipe: "Kører {{name}}",
      spawn: "Kigger på flere ting på én gang",
      ask: "Spørger dig",
      other: "Arbejder",
    },
    failedStep: "Det lykkedes ikke: {{reason}}",
    asks: "Adea spørger",
    asked: "Du svarede: {{answer}}",
    failed: "Adea kunne ikke gøre svaret færdigt.",
    retry: "Prøv igen",
    stopped: "Stoppet, før det var færdigt.",
    followUp: "Spørg videre",
    send: "Send",
    stop: "Stop",
    attach: "Tilføj et billede",
    removeAttachment: "Fjern billedet",
    picture: "Et billede, du har tilføjet",
    keep: "Gem det her",
    askAbout: "Spørg om det her",
    source: "Kilde",
    sources: "Det bygger på",
    explore: "Udforsk",
  },
};

export default defineCopy({
  base: true,
  en,
  da,
});
